import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Key, Smartphone, AlertTriangle, Check, X, 
  Loader2, Copy, Eye, EyeOff, LogOut, Clock, Globe,
  Monitor, RefreshCw, Lock, Unlock, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecuritySettings() {
  const [loading, setLoading] = useState(true);
  const [twoFaStatus, setTwoFaStatus] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [showDisable, setShowDisable] = useState(false);
  
  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [provider, setProvider] = useState('email');
  
  // Change authenticator states
  const [showChangeAuthenticator, setShowChangeAuthenticator] = useState(false);
  const [changeAuthPassword, setChangeAuthPassword] = useState('');
  const [changeAuthLoading, setChangeAuthLoading] = useState(false);
  const [newAuthenticatorData, setNewAuthenticatorData] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchSessions();
    fetchPasswordStatus();
  }, []);

  const fetchPasswordStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/security/has-password`, {
        withCredentials: true
      });
      setHasPassword(response.data.has_password);
      setProvider(response.data.provider || 'email');
    } catch (error) {
      console.error('Error fetching password status:', error);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/security/2fa/status`, {
        withCredentials: true
      });
      setTwoFaStatus(response.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    setSessionsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/security/sessions`, {
        withCredentials: true
      });
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const startSetup = async () => {
    setSetupLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/security/2fa/setup`, {}, {
        withCredentials: true
      });
      setSetupData(response.data);
      toast.success('QR Code gerado! Escaneie com seu autenticador.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar configuração');
    } finally {
      setSetupLoading(false);
    }
  };

  const verifySetup = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos do autenticador');
      return;
    }
    
    setVerifyLoading(true);
    try {
      await axios.post(`${API_URL}/api/security/2fa/verify-setup`, {
        code: verifyCode
      }, {
        withCredentials: true
      });
      toast.success('2FA ativado com sucesso!');
      setSetupData(null);
      setVerifyCode('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifyLoading(false);
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos do autenticador');
      return;
    }
    
    setVerifyLoading(true);
    try {
      await axios.post(`${API_URL}/api/security/2fa/disable`, {
        code: disableCode
      }, {
        withCredentials: true
      });
      toast.success('2FA desativado');
      setShowDisable(false);
      setDisableCode('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setVerifyLoading(false);
    }
  };

  const revokeAllSessions = async () => {
    if (!window.confirm('Isso vai encerrar todas as outras sessões. Continuar?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_URL}/api/security/sessions/revoke-all`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchSessions();
    } catch (error) {
      toast.error('Erro ao encerrar sessões');
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        withCredentials: true
      });
      toast.success('Senha alterada com sucesso!');
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle create password (for OAuth users)
  const handleCreatePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API_URL}/api/security/create-password`, {
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword
      }, {
        withCredentials: true
      });
      toast.success('Senha criada com sucesso! Agora você pode fazer login com email e senha.');
      setShowPasswordChange(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setHasPassword(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle change authenticator
  const handleChangeAuthenticator = async (e) => {
    e.preventDefault();
    
    if (!changeAuthPassword) {
      toast.error('Digite sua senha para confirmar');
      return;
    }
    
    setChangeAuthLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/security/2fa/change-authenticator`, {
        password: changeAuthPassword
      }, {
        withCredentials: true
      });
      
      setNewAuthenticatorData(response.data);
      setChangeAuthPassword('');
      toast.success('Novo autenticador configurado! Escaneie o QR Code.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao trocar autenticador');
    } finally {
      setChangeAuthLoading(false);
    }
  };

  const finishAuthenticatorChange = () => {
    setShowChangeAuthenticator(false);
    setNewAuthenticatorData(null);
    setChangeAuthPassword('');
    fetchStatus();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-amber-500/20 rounded-xl">
            <Shield className="w-8 h-8 text-amber-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Segurança da Conta</h1>
            <p className="text-stone-400">Gerencie a segurança da sua conta Author's Key</p>
          </div>
        </div>

        {/* Password Section */}
        <div className="bg-stone-900/50 rounded-xl border border-stone-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Lock className="w-6 h-6 text-amber-400" />
              <div>
                <h2 className="text-lg font-semibold">
                  {hasPassword ? 'Alterar Senha' : 'Criar Senha'}
                </h2>
                <p className="text-sm text-stone-400">
                  {hasPassword 
                    ? 'Atualize a senha da sua conta'
                    : `Você entrou via ${provider === 'google' ? 'Google' : 'rede social'}. Crie uma senha para também poder fazer login com email.`
                  }
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowPasswordChange(!showPasswordChange)}
              variant="outline"
              className="border-stone-700 hover:bg-stone-800"
            >
              {showPasswordChange ? 'Cancelar' : (hasPassword ? 'Alterar' : 'Criar')}
              <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${showPasswordChange ? 'rotate-90' : ''}`} />
            </Button>
          </div>

          {showPasswordChange && (
            <form onSubmit={hasPassword ? handlePasswordChange : handleCreatePassword} className="mt-4 space-y-4">
              {/* Campo de senha atual - só mostra se usuário JÁ TEM senha */}
              {hasPassword && (
                <div>
                  <label className="block text-sm font-medium mb-1 text-stone-300">Senha Atual</label>
                  <div className="relative">
                    <Input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      placeholder="Digite sua senha atual"
                      className="bg-stone-800 border-stone-700 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-300"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}

              {/* Info box para usuários OAuth */}
              {!hasPassword && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200">
                  <p>Ao criar uma senha, você poderá fazer login tanto com Google quanto com email e senha.</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1 text-stone-300">
                  {hasPassword ? 'Nova Senha' : 'Senha'}
                </label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    placeholder={hasPassword ? "Digite a nova senha (mín. 6 caracteres)" : "Crie uma senha (mín. 6 caracteres)"}
                    className="bg-stone-800 border-stone-700 pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-300"
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-stone-300">Confirmar Senha</label>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  placeholder="Confirme a senha"
                  className="bg-stone-800 border-stone-700"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={passwordLoading}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                {passwordLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {hasPassword ? 'Salvar Nova Senha' : 'Criar Senha'}
              </Button>
            </form>
          )}
        </div>

        {/* 2FA Section */}
        <div className="bg-stone-900/50 rounded-xl border border-stone-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Smartphone className="w-6 h-6 text-blue-400" />
              <div>
                <h2 className="text-lg font-semibold">Autenticação em Dois Fatores (2FA)</h2>
                <p className="text-sm text-stone-400">Proteja sua conta com um app autenticador</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              twoFaStatus?.enabled 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-stone-700 text-stone-400'
            }`}>
              {twoFaStatus?.enabled ? 'Ativo' : 'Inativo'}
            </div>
          </div>

          {/* 2FA Not Enabled - Show Setup */}
          {!twoFaStatus?.enabled && !setupData && (
            <div className="mt-4">
              <p className="text-stone-400 mb-4">
                A autenticação em dois fatores adiciona uma camada extra de segurança à sua conta. 
                Você precisará de um app autenticador como Google Authenticator, Authy ou 1Password.
              </p>
              <Button
                onClick={startSetup}
                disabled={setupLoading}
                className="bg-amber-500 hover:bg-amber-600 text-stone-900"
              >
                {setupLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Key className="w-4 h-4 mr-2" />
                )}
                Configurar 2FA
              </Button>
            </div>
          )}

          {/* 2FA Setup in Progress */}
          {setupData && (
            <div className="mt-4 space-y-6">
              {/* Step 1: QR Code */}
              <div className="p-4 bg-stone-800/50 rounded-lg">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 bg-amber-500 text-stone-900 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Escaneie o QR Code
                </h3>
                <p className="text-sm text-stone-400 mb-4">
                  Abra seu app autenticador e escaneie este código:
                </p>
                <div className="flex justify-center bg-white p-4 rounded-lg w-fit mx-auto">
                  <img src={setupData.qr_code} alt="QR Code" className="w-48 h-48" />
                </div>
                <div className="mt-4 p-3 bg-stone-900 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Ou digite manualmente:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-amber-400 flex-1 break-all">{setupData.secret}</code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(setupData.secret)}
                    >
                      <Copy size={14} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Step 2: Recovery Codes */}
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-red-400">
                  <span className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Guarde seus Códigos de Recuperação
                </h3>
                <p className="text-sm text-stone-300 mb-4">
                  <strong>IMPORTANTE:</strong> Anote estes códigos em papel e guarde em local seguro. 
                  Eles são a única forma de acessar sua conta se você perder o autenticador.
                </p>
                <p className="text-xs text-red-400 mb-4">
                  Nunca tire foto, salve no computador ou envie por email.
                </p>
                
                <Button
                  variant="outline"
                  onClick={() => setShowRecoveryCodes(!showRecoveryCodes)}
                  className="mb-4 border-red-500/50 text-red-400"
                >
                  {showRecoveryCodes ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
                  {showRecoveryCodes ? 'Ocultar Códigos' : 'Mostrar Códigos'}
                </Button>
                
                {showRecoveryCodes && (
                  <div className="grid grid-cols-2 gap-2 p-4 bg-stone-900 rounded-lg">
                    {setupData.recovery_codes.map((code, i) => (
                      <code key={i} className="text-sm font-mono text-amber-400 p-2 bg-stone-800 rounded">
                        {code}
                      </code>
                    ))}
                  </div>
                )}
              </div>

              {/* Step 3: Verify */}
              <div className="p-4 bg-stone-800/50 rounded-lg">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <span className="w-6 h-6 bg-amber-500 text-stone-900 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  Confirme a Configuração
                </h3>
                <p className="text-sm text-stone-400 mb-4">
                  Digite o código de 6 dígitos exibido no seu autenticador:
                </p>
                <div className="flex gap-3">
                  <Input
                    type="text"
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-40 text-center text-2xl tracking-widest bg-stone-800 border-stone-700"
                    maxLength={6}
                  />
                  <Button
                    onClick={verifySetup}
                    disabled={verifyLoading || verifyCode.length !== 6}
                    className="bg-green-500 hover:bg-green-600 text-white"
                  >
                    {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check size={18} />}
                    Ativar 2FA
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSetupData(null);
                      setVerifyCode('');
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 2FA Enabled - Show Status and Disable Option */}
          {twoFaStatus?.enabled && !setupData && (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-green-400 mb-4">
                <Check className="w-5 h-5" />
                <span>Sua conta está protegida com autenticação em dois fatores.</span>
              </div>
              <p className="text-sm text-stone-400 mb-4">
                Configurado em: {new Date(twoFaStatus.setup_at).toLocaleDateString('pt-BR')}
              </p>
              
              {/* Change Authenticator Section */}
              {showChangeAuthenticator ? (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                  {!newAuthenticatorData ? (
                    <>
                      <p className="text-sm text-amber-400 mb-3">
                        Digite sua senha para trocar de autenticador:
                      </p>
                      <form onSubmit={handleChangeAuthenticator} className="flex gap-3 flex-wrap">
                        <Input
                          type="password"
                          placeholder="Sua senha atual"
                          value={changeAuthPassword}
                          onChange={(e) => setChangeAuthPassword(e.target.value)}
                          className="w-52 bg-stone-800 border-stone-700"
                        />
                        <Button
                          type="submit"
                          disabled={changeAuthLoading || !changeAuthPassword}
                          className="bg-amber-500 hover:bg-amber-600 text-stone-900"
                        >
                          {changeAuthLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setShowChangeAuthenticator(false);
                            setChangeAuthPassword('');
                          }}
                        >
                          Cancelar
                        </Button>
                      </form>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-green-400 mb-3">
                        ✓ Novo autenticador configurado! Escaneie o QR Code abaixo:
                      </p>
                      
                      {/* New QR Code */}
                      <div className="bg-white rounded-lg p-4 mb-4 flex justify-center w-fit mx-auto">
                        <img 
                          src={newAuthenticatorData.qr_code}
                          alt="Novo QR Code"
                          className="w-48 h-48"
                        />
                      </div>
                      
                      {/* Manual Key */}
                      <div className="bg-stone-800 rounded-lg p-3 mb-4">
                        <p className="text-xs text-stone-400 mb-1">Chave manual:</p>
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-sm text-stone-200 break-all select-all flex-1">
                            {newAuthenticatorData.secret}
                          </code>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => copyToClipboard(newAuthenticatorData.secret)}
                          >
                            <Copy size={14} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* New Recovery Codes */}
                      {newAuthenticatorData.recovery_codes?.length > 0 && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                          <p className="text-sm font-medium text-red-400 mb-2 flex items-center gap-2">
                            <AlertTriangle size={16} />
                            Novos Códigos de Recuperação
                          </p>
                          <p className="text-xs text-stone-400 mb-3">
                            Guarde estes códigos em local seguro. Os códigos anteriores foram invalidados.
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {newAuthenticatorData.recovery_codes.map((code, i) => (
                              <code key={i} className="bg-stone-800 p-2 rounded text-center font-mono text-sm">
                                {code}
                              </code>
                            ))}
                          </div>
                          <Button
                            className="w-full mt-3"
                            variant="outline"
                            onClick={() => copyToClipboard(newAuthenticatorData.recovery_codes.join('\n'))}
                          >
                            <Copy size={14} className="mr-2" />
                            Copiar todos os códigos
                          </Button>
                        </div>
                      )}
                      
                      <Button
                        onClick={finishAuthenticatorChange}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Check size={16} className="mr-2" />
                        Concluir
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex gap-3 mb-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowChangeAuthenticator(true)}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Trocar Autenticador
                  </Button>
                </div>
              )}
              
              {!showDisable && !showChangeAuthenticator && (
                <Button
                  variant="outline"
                  onClick={() => setShowDisable(true)}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Unlock size={16} className="mr-2" />
                  Desativar 2FA
                </Button>
              )}
              
              {showDisable && !showChangeAuthenticator && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-sm text-red-400 mb-3">
                    Para desativar o 2FA, digite o código atual do seu autenticador:
                  </p>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      placeholder="000000"
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-40 text-center text-xl tracking-widest bg-stone-800 border-stone-700"
                      maxLength={6}
                    />
                    <Button
                      onClick={disable2FA}
                      disabled={verifyLoading || disableCode.length !== 6}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desativar'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setShowDisable(false);
                        setDisableCode('');
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active Sessions */}
        <div className="bg-stone-900/50 rounded-xl border border-stone-800 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Monitor className="w-6 h-6 text-purple-400" />
              <div>
                <h2 className="text-lg font-semibold">Sessões Ativas</h2>
                <p className="text-sm text-stone-400">Dispositivos conectados à sua conta</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={revokeAllSessions}
              className="border-red-500/50 text-red-400"
            >
              <LogOut size={14} className="mr-1" />
              Encerrar Outras
            </Button>
          </div>

          {sessionsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map((session, i) => (
                <div 
                  key={i} 
                  className={`p-4 rounded-lg border ${
                    session.is_current 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-stone-800/50 border-stone-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-stone-400" />
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {session.ip_address || 'IP desconhecido'}
                          {session.is_current && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                              Sessão atual
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-stone-500 truncate max-w-md">
                          {session.user_agent || 'Dispositivo desconhecido'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-stone-400">
                      <div className="flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(session.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-center text-stone-500 py-4">Nenhuma sessão ativa</p>
              )}
            </div>
          )}
        </div>

        {/* Security Tips */}
        <div className="bg-stone-900/50 rounded-xl border border-stone-800 p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
            <h2 className="text-lg font-semibold">Dicas de Segurança</h2>
          </div>
          <ul className="space-y-2 text-sm text-stone-400">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              Use uma senha forte e única para sua conta
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              Mantenha o 2FA ativo para proteção máxima
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              Guarde os códigos de recuperação em local seguro
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              Revise regularmente as sessões ativas
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              Nunca compartilhe sua senha ou códigos de recuperação
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
